import { Stack, Typography, Alert, Skeleton } from "@mui/material";
import { usePartyIdentificationImagesQuery } from "Api/Queries/Party/usePartyIdentificationImagesQuery";
import { PartyIdentificationImageType } from "Infrastructure/Api/Api";
import { ImageCard } from "./ImageCard";
import { Resources, useResource } from "Translations/Resources";

const IMAGE_TYPES_BY_GROUP: Record<
  "first" | "second",
  ReadonlyArray<PartyIdentificationImageType>
> = {
  first: [
    PartyIdentificationImageType.FirstIDCardFrontSide,
    PartyIdentificationImageType.FirstIDCardBackSide,
  ],
  second: [
    PartyIdentificationImageType.SecondIDCardFrontSide,
    PartyIdentificationImageType.SecondIDCardBackSide,
  ],
};

type Props = {
  partyPublicID: string;
  fileNamePrefix: string;
  documentGroup?: "first" | "second";
};

export const PartyIdentificationImagesViewer = ({
  partyPublicID,
  fileNamePrefix: _unused,
  documentGroup,
}: Props) => {
  const { t } = useResource();
  const { data, isLoading, error } = usePartyIdentificationImagesQuery({
    publicID: partyPublicID,
  });

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4} gap={1}>
        <Skeleton variant="rectangular" width="100%" height={60} />
        <Skeleton variant="rectangular" width="100%" height={60} />
        <Skeleton variant="rectangular" width="100%" height={60} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {t(Resources.Error.DocumentsFailedToLoad)}
      </Alert>
      /* For now we're not letting users download identification images, might change later */
      /* <DownloadPartyIdentificationsButton
          fileNamePrefix={fileNamePrefix}
          partyPublicID={partyPublicID}
        /> */
    );
  }

  const allImages = data?.images || [];
  const images = documentGroup
    ? allImages.filter((image) =>
        IMAGE_TYPES_BY_GROUP[documentGroup].includes(image.imageType),
      )
    : allImages;

  if (images.length === 0) {
    return <Typography>{t(Resources.AML.NoDocuments)}</Typography>;
  }

  return (
    <Stack spacing={2} sx={{ "@media print": { display: "none" } }}>
      {images.map((image) => (
        <ImageCard key={image.imageType} image={image} />
      ))}
      {/* For now we're not letting users download identification images, might change later */}
      {/* <DownloadPartyIdentificationsButton
        fileNamePrefix={fileNamePrefix}
        partyPublicID={partyPublicID}
      /> */}
    </Stack>
  );
};
